import { Almanac } from "protos/robot_syncer/plant_profile";
import {
  type AlmanacGroup,
  useAlmanacOptions,
} from "portal/components/categoryTemplates/useAlmanacOptions";
import { AlmanacMostSimilarFields } from "portal/components/categoryTemplates/createCategory/AlmanacMostSimilarFields";
import {
  AlmanacUpdate,
  MOST_SIMILAR_TO_OPTIONS,
  mostSimilarToForOriginCategoryId,
} from "portal/utils/categoryCollection/category";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
} from "@mui/material";
import { Form, Formik, useFormikContext } from "formik";
import { Formula } from "protos/almanac/almanac";
import {
  formulasOnExistingBands,
  withoutFineTuneFields,
} from "portal/utils/shootTime";
import { isEqual } from "common/utils/equality";
import { resolveAlmanacCategory } from "portal/components/categoryTemplates/createCategory/resolveAlmanacCategory";
import { useTranslation } from "react-i18next";
import React, { FC, useCallback, useMemo } from "react";

type ShootTimeResetFormValues = {
  sourceAlmanacId: string;
  mostSimilarTo: string;
};

/** Source formulas on the draft's bands, with FTI and FTM cleared. */
const resetFormulas = (
  currentFormulas: readonly Formula[],
  sourceFormulas: readonly Formula[]
): Formula[] =>
  withoutFineTuneFields(
    formulasOnExistingBands(currentFormulas, sourceFormulas)
  );

interface ShootTimeResetDialogProps {
  almanac: Almanac;
  customerUuid?: string;
  onApply: (update: AlmanacUpdate) => void;
  onClose: () => void;
}

export const ShootTimeResetDialog: FC<ShootTimeResetDialogProps> = ({
  almanac,
  customerUuid,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const { almanacGroups, defaultAlmanacId, isLoading } =
    useAlmanacOptions(customerUuid);
  const initialValues = useMemo(
    (): ShootTimeResetFormValues => ({
      sourceAlmanacId: defaultAlmanacId,
      mostSimilarTo:
        mostSimilarToForOriginCategoryId(almanac.originCategoryId) ??
        MOST_SIMILAR_TO_OPTIONS[0],
    }),
    [almanac.originCategoryId, defaultAlmanacId]
  );

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{t("components.shootTime.reset.title")}</DialogTitle>
      {isLoading ? (
        <DialogContent>
          <div className="flex flex-col gap-4">
            <Skeleton variant="rectangular" className="h-14 w-full rounded" />
            <Skeleton variant="rectangular" className="h-14 w-full rounded" />
          </div>
        </DialogContent>
      ) : (
        <Formik<ShootTimeResetFormValues>
          initialValues={initialValues}
          onSubmit={(values, { setFieldError }) => {
            const resolved = resolveAlmanacCategory(
              almanacGroups,
              values.sourceAlmanacId,
              values.mostSimilarTo,
              t
            );
            if (!resolved.ok) {
              setFieldError(resolved.field, resolved.message);
              return;
            }
            onApply({
              formulas: resetFormulas(
                almanac.formulas,
                resolved.almanac.formulas
              ),
              originCategoryId: resolved.almanac.originCategoryId,
            });
            onClose();
          }}
        >
          <ShootTimeResetForm
            almanac={almanac}
            almanacGroups={almanacGroups}
            defaultAlmanacId={defaultAlmanacId}
            onClose={onClose}
          />
        </Formik>
      )}
    </Dialog>
  );
};

const ShootTimeResetForm: FC<{
  almanac: Almanac;
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
  onClose: () => void;
}> = ({ almanac, almanacGroups, defaultAlmanacId, onClose }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<ShootTimeResetFormValues>();

  const currentMostSimilarTo = useMemo(() => {
    const currentFormulas = withoutFineTuneFields(almanac.formulas);
    const matching = new Set<string>();
    for (const option of MOST_SIMILAR_TO_OPTIONS) {
      const resolved = resolveAlmanacCategory(
        almanacGroups,
        values.sourceAlmanacId,
        option,
        t
      );
      if (
        resolved.ok &&
        resolved.almanac.originCategoryId === almanac.originCategoryId &&
        isEqual(
          resetFormulas(almanac.formulas, resolved.almanac.formulas),
          currentFormulas,
          { orderedArrays: true }
        )
      ) {
        matching.add(option);
      }
    }
    return matching;
  }, [almanac, almanacGroups, t, values.sourceAlmanacId]);

  const isMostSimilarCurrent = useCallback(
    (mostSimilarTo: string) => currentMostSimilarTo.has(mostSimilarTo),
    [currentMostSimilarTo]
  );

  const applyDisabled =
    !defaultAlmanacId || isMostSimilarCurrent(values.mostSimilarTo);

  return (
    <Form>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <AlmanacMostSimilarFields
            almanacGroups={almanacGroups}
            defaultAlmanacId={defaultAlmanacId}
            isMostSimilarCurrent={isMostSimilarCurrent}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" type="button" onClick={onClose}>
          {t("utils.actions.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={applyDisabled}
        >
          {t("utils.actions.apply")}
        </Button>
      </DialogActions>
    </Form>
  );
};
